import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  RefreshCw,
  Edit3,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Lightbulb,
  Target,
  Building,
  Calendar,
  Loader2,
  Briefcase,
  Wand2,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  Share2,
  Trophy,
  AlertCircle,
  Loader as LoaderIcon,
  Sparkles,
} from 'lucide-react';

import { logger } from '../utils/logger';
import { API } from '../utils/apiBase';
import { useFeatureGate } from '../utils/useFeatureGate';
import ResumeStudio from './ResumeStudio';


// Share the ATS score publicly (creates a shareable /score/:id link with OG tags)
const ShareScoreButton = ({ resume }) => {
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);

  // Derive candidate name: use first line of resume content
  const getCandidateName = () => {
    const firstLine = (resume?.content || '').split('\n').map(l => l.trim()).find(l => l.length > 0);
    if (firstLine && firstLine.length < 60 && !firstLine.includes('@')) return firstLine;
    return resume?.title || 'Ascend Candidate';
  };

  const create = async () => {
    if (shareUrl || loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/share/score`, {
        name: getCandidateName(),
        score: resume.ats_score || 0,
        role: resume.target_job || resume.title || 'Resume',
        top_keywords: (resume.keywords_matched || []).slice(0, 10),
      }, { withCredentials: true });
      setShareUrl(res.data.share_url);
    } catch (err) {
      logger.error('Share create failed:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error(err.message);
    }
  };

  const shareToLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener,noreferrer');
  };

  if (!resume?.ats_score) return null;

  return (
    <div className="mt-5 pt-5 border-t border-[#e5e7eb]">
      {!shareUrl ? (
        <button
          onClick={create}
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#002fa7] text-[#002fa7] font-semibold text-sm hover:bg-[#002fa7]/5 transition-colors disabled:opacity-60"
          data-testid="share-score-btn"
        >
          {loading ? <><LoaderIcon size={14} className="animate-spin" /> Creating…</> : <><Trophy size={14} /> Share My ATS Score</>}
        </button>
      ) : (
        <div className="space-y-2" data-testid="share-score-result">
          <p className="text-xs font-bold uppercase tracking-wider text-[#6b7280]">Your Share Link</p>
          <div className="flex gap-1">
            <input readOnly value={shareUrl} onFocus={(e) => e.target.select()} className="flex-1 px-2 py-1.5 text-xs border border-[#e5e7eb] font-mono bg-[#f4f5f7]" data-testid="share-score-url" />
            <button onClick={copy} className={`px-3 py-1.5 text-xs font-semibold transition-colors ${copied ? 'bg-[#10b981] text-white' : 'bg-[#0f1115] text-white hover:bg-[#002fa7]'}`} data-testid="share-score-copy">
              {copied ? <Check size={12} /> : <Copy size={12} />}
            </button>
          </div>
          <button onClick={shareToLinkedIn} className="w-full px-3 py-2 bg-[#0077b5] text-white text-xs font-semibold hover:bg-[#005a8a] transition-colors inline-flex items-center justify-center gap-2" data-testid="share-score-linkedin">
            <Share2 size={12} /> Share on LinkedIn
          </button>
        </div>
      )}
    </div>
  );
};

// Shared motion variants
const fadeInUp = { opacity: 0, y: 20 };
const fadeInUpAnimate = { opacity: 1, y: 0 };
const delayedTransition = { delay: 0.1 };

// Helper function for score colors
const getScoreColor = (score) => {
  if (score >= 75) return '#10b981';
  if (score < 50) return '#e11d48';
  return '#eab308';
};

const getScoreStyles = (score) => {
  if (score < 50) return { text: 'text-[#e11d48]', bg: 'bg-[#e11d48]', label: 'Needs Work' };
  if (score <= 75) return { text: 'text-[#eab308]', bg: 'bg-[#eab308]', label: 'Good' };
  return { text: 'text-[#10b981]', bg: 'bg-[#10b981]', label: 'Excellent' };
};

// Score Gauge Component
const ScoreGauge = ({ score, label = "ATS Score" }) => {
  const colors = getScoreStyles(score);
  const circumference = 2 * Math.PI * 54;
  const dashOffset = circumference - (score / 100) * circumference;
  const strokeColor = getScoreColor(score);

  return (
    <div className="text-center">
      <div className="relative w-32 h-32 mx-auto mb-4">
        <svg className="w-full h-full transform -rotate-90">
          <circle cx="64" cy="64" r="54" fill="none" stroke="#e5e7eb" strokeWidth="12" />
          <circle
            cx="64" cy="64" r="54" fill="none"
            stroke={strokeColor} strokeWidth="12"
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            strokeLinecap="round" className="transition-all duration-1000"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-4xl font-satoshi font-black ${colors.text}`}>{score}</span>
          <span className="text-xs text-[#6b7280] uppercase tracking-wider">{label}</span>
        </div>
      </div>
      <div className={`inline-block px-4 py-1 text-sm font-semibold ${colors.bg} text-white`}>
        {colors.label}
      </div>
    </div>
  );
};

// Rewrite Card Component
const RewriteCard = ({ rewrite, index, selected, onToggleSelect, onCopy }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(rewrite.improved);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    if (onCopy) onCopy();
  };

  return (
    <div
      className={`border p-4 mb-4 transition-colors ${selected ? 'border-[#10b981] bg-[#10b981]/5' : 'border-[#e5e7eb] bg-white'}`}
      data-testid={`rewrite-card-${index}`}
    >
      <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!!selected}
          onChange={() => onToggleSelect?.(index)}
          className="w-4 h-4 accent-[#10b981]"
          data-testid={`rewrite-select-${index}`}
        />
        <span className="text-sm font-semibold text-[#0f1115]">
          Apply this rewrite to my resume
        </span>
      </label>
      <div className="mb-3">
        <span className="text-xs uppercase tracking-wider text-[#e11d48] font-semibold">Original</span>
        <p className="text-sm text-[#6b7280] mt-1 line-through">{rewrite.original}</p>
      </div>
      <div className="mb-3">
        <span className="text-xs uppercase tracking-wider text-[#10b981] font-semibold">Improved</span>
        <p className="text-sm text-[#0f1115] mt-1 font-medium">{rewrite.improved}</p>
      </div>
      {rewrite.reason && (
        <p className="text-xs text-[#6b7280] italic mb-3">Why: {rewrite.reason}</p>
      )}
      <button
        onClick={handleCopy}
        className="text-sm text-[#002fa7] hover:underline flex items-center space-x-1"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        <span>{copied ? 'Copied!' : 'Copy improved text'}</span>
      </button>
    </div>
  );
};

// Keywords Panel Component
const KeywordsPanel = ({ title, keywords, icon: Icon, iconColor, tagBg, tagText, emptyMessage }) => (
  <motion.div initial={fadeInUp} animate={fadeInUpAnimate} className="dashboard-card p-6">
    <h3 className="font-satoshi text-lg font-bold text-[#0f1115] mb-4 flex items-center space-x-2">
      <Icon size={20} className={iconColor} />
      <span>{title} ({keywords?.length || 0})</span>
    </h3>
    <div className="flex flex-wrap gap-2">
      {keywords?.map((keyword) => (
        <span key={keyword} className={`px-3 py-1 ${tagBg} ${tagText} text-sm font-medium`}>{keyword}</span>
      ))}
      {(!keywords || keywords.length === 0) && (
        <span className="text-[#6b7280] text-sm">{emptyMessage}</span>
      )}
    </div>
  </motion.div>
);

// Suggestions Panel Component
const SuggestionsPanel = ({ suggestions }) => (
  <motion.div initial={fadeInUp} animate={fadeInUpAnimate} className="dashboard-card p-6">
    <h3 className="font-satoshi text-lg font-bold text-[#0f1115] mb-4 flex items-center space-x-2">
      <Lightbulb size={20} className="text-[#eab308]" />
      <span>Suggestions</span>
    </h3>
    <ul className="space-y-3">
      {suggestions?.map((suggestion) => (
        <li key={suggestion} className="flex items-start space-x-3">
          <AlertTriangle size={16} className="text-[#eab308] mt-1 flex-shrink-0" />
          <span className="text-sm text-[#4b5563]">{suggestion}</span>
        </li>
      ))}
      {(!suggestions || suggestions.length === 0) && (
        <li className="text-[#6b7280] text-sm">Great job! No suggestions at this time.</li>
      )}
    </ul>
  </motion.div>
);

// Job Matcher Panel Component
const JobMatcherPanel = ({ showJobMatcher, setShowJobMatcher, jobDescription, setJobDescription, matchingJob, handleMatchJob }) => (
  <motion.div initial={fadeInUp} animate={fadeInUpAnimate} className="dashboard-card p-6 border-2 border-[#002fa7]">
    <button onClick={() => setShowJobMatcher(!showJobMatcher)} className="w-full flex items-center justify-between">
      <h3 className="font-satoshi text-lg font-bold text-[#0f1115] flex items-center space-x-2">
        <Briefcase size={20} className="text-[#002fa7]" />
        <span>Job Description Matcher</span>
        <span className="text-xs bg-[#002fa7] text-white px-2 py-0.5 rounded-full">NEW</span>
      </h3>
      {showJobMatcher ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
    </button>
    {showJobMatcher && (
      <div className="mt-4">
        <textarea
          value={jobDescription}
          onChange={(e) => setJobDescription(e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border border-[#e5e7eb] bg-white text-[#0f1115] text-sm focus:border-[#002fa7]"
          placeholder="Paste the job description here to get precise matching..."
        />
        <button
          onClick={handleMatchJob}
          disabled={matchingJob || !jobDescription.trim()}
          className="mt-3 w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50"
          data-testid="match-job-button"
        >
          {matchingJob ? <Loader2 size={18} className="animate-spin" /> : <Target size={18} />}
          <span>{matchingJob ? 'Analyzing...' : 'Match Against Job'}</span>
        </button>
      </div>
    )}
  </motion.div>
);

// AI Rewriter Panel Component
const AIRewriterPanel = ({ rewriting, handleRewrite, rewrites, showRewrites, setShowRewrites }) => (
  <motion.div initial={fadeInUp} animate={fadeInUpAnimate} className="dashboard-card p-6 border-2 border-[#10b981]">
    <h3 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center space-x-2">
      <Wand2 size={20} className="text-[#10b981]" />
      <span>AI Resume Rewriter</span>
      <span className="text-xs bg-[#10b981] text-white px-2 py-0.5 rounded-full">NEW</span>
    </h3>
    <p className="text-sm text-[#6b7280] mb-4">Get AI-powered rewrites for weak bullet points with metrics and impact.</p>
    <button
      onClick={handleRewrite}
      disabled={rewriting}
      className="w-full py-3 bg-[#10b981] text-white font-semibold hover:bg-[#059669] transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
      data-testid="rewrite-button"
    >
      {rewriting ? <Loader2 size={18} className="animate-spin" /> : <Wand2 size={18} />}
      <span>{rewriting ? 'Rewriting...' : 'Generate Rewrites'}</span>
    </button>
    {rewrites.length > 0 && (
      <button onClick={() => setShowRewrites(!showRewrites)} className="mt-3 text-sm text-[#002fa7] hover:underline">
        {showRewrites ? 'Hide' : 'Show'} {rewrites.length} rewrites
      </button>
    )}
  </motion.div>
);

// Resume accent color options — professional, Word-safe colors
export const ACCENT_COLORS = [
  { name: 'Navy Blue', value: '#002fa7' },
  { name: 'Charcoal', value: '#1f2937' },
  { name: 'Burgundy', value: '#7f1d1d' },
  { name: 'Forest Green', value: '#14532d' },
  { name: 'Deep Teal', value: '#134e4a' },
  { name: 'Classic Black', value: '#000000' },
];

// Copy Resume Panel Component
const CopyResumePanel = ({ copied, handleCopy, handlePreview, hasRewrites, accentColor, setAccentColor, handleDownload, onOpenStudio }) => (
  <motion.div initial={fadeInUp} animate={fadeInUpAnimate} className="dashboard-card p-6 border-2" style={{ borderColor: accentColor }}>
    <h3 className="font-satoshi text-lg font-bold text-[#0f1115] mb-2 flex items-center space-x-2">
      <Copy size={20} style={{ color: accentColor }} />
      <span>Export Resume</span>
    </h3>
    <p className="text-sm text-[#6b7280] mb-3">
      {hasRewrites
        ? 'Copy your resume with AI rewrites applied — formatted and ready for Word or Google Docs.'
        : 'Copy your resume with professional formatting — paste directly into Word or Google Docs.'}
    </p>

    <div className="mb-3 p-3 bg-[#f4f5f7]">
      <label className="text-xs font-bold text-[#6b7280] uppercase tracking-wider block mb-2">Accent Color</label>
      <div className="flex flex-wrap gap-2" data-testid="accent-color-picker">
        {ACCENT_COLORS.map(c => (
          <button
            key={c.value}
            type="button"
            onClick={() => setAccentColor(c.value)}
            title={c.name}
            aria-label={`Accent color ${c.name}`}
            className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${accentColor === c.value ? 'ring-2 ring-offset-2 ring-[#0f1115]' : 'border-white'}`}
            style={{ backgroundColor: c.value, borderColor: accentColor === c.value ? c.value : '#e5e7eb' }}
            data-testid={`accent-${c.name.toLowerCase().replace(/\s/g, '-')}`}
          />
        ))}
      </div>
    </div>

    <div className="space-y-2">
      <button
        onClick={handleCopy}
        className={`w-full py-3 font-semibold transition-colors flex items-center justify-center space-x-2 text-white`}
        style={{ backgroundColor: copied ? '#10b981' : accentColor }}
        data-testid="copy-resume-button"
      >
        {copied ? <Check size={18} /> : <Copy size={18} />}
        <span>{copied ? 'Copied — Paste into Word!' : 'Copy Formatted Resume'}</span>
      </button>

      {/* Native downloads — no paste needed, guaranteed color fidelity */}
      {handleDownload && (
        <div className="grid grid-cols-2 gap-2 pt-1">
          <button
            onClick={() => handleDownload('docx')}
            className="py-2 text-sm font-semibold transition-colors flex items-center justify-center space-x-2 border"
            style={{ color: accentColor, borderColor: accentColor }}
            data-testid="download-docx-button"
          >
            <FileText size={14} />
            <span>Download .docx</span>
          </button>
          <button
            onClick={() => handleDownload('pdf')}
            className="py-2 text-sm font-semibold transition-colors flex items-center justify-center space-x-2 border"
            style={{ color: accentColor, borderColor: accentColor }}
            data-testid="download-pdf-button"
          >
            <FileText size={14} />
            <span>Download PDF</span>
          </button>
        </div>
      )}

      <button
        onClick={handlePreview}
        className="w-full py-2 text-sm hover:bg-[#f4f5f7] transition-colors flex items-center justify-center space-x-1 border border-[#e5e7eb]"
        style={{ color: accentColor }}
        data-testid="preview-formatted-btn"
      >
        <FileText size={14} />
        <span>Preview Formatted Version</span>
      </button>

      <Link
        to="#"
        onClick={(e) => { e.preventDefault(); onOpenStudio?.(); }}
        className="w-full py-2.5 text-sm font-semibold transition-colors flex items-center justify-center space-x-2 text-white cursor-pointer"
        style={{ background: accentColor }}
        data-testid="open-studio-btn"
      >
        <Sparkles size={14} />
        <span>Open in Resume Studio</span>
      </Link>
    </div>
    <div className="mt-3 p-2 bg-[#fffbea] border border-[#fde68a] text-[11px] text-[#78350f] leading-relaxed" data-testid="paste-tip">
      <strong>Pro tip:</strong> After pasting into Word, <strong>right-click</strong> and choose <strong>&ldquo;Keep Source Formatting&rdquo;</strong> (or press <strong>Ctrl+Alt+V</strong> &rarr; &ldquo;HTML Format&rdquo;) to preserve your accent color and layout exactly.
    </div>
  </motion.div>
);

// Target Length Panel — shows current target + lets user change it.
// Also houses "Re-optimize" (re-score) and "Edit" controls so all length/content
// actions live in the same place.
const TargetLengthPanel = ({ resume, reoptimizing, onChange, onReoptimize, onToggleEdit, editing }) => {
  const current = resume.target_pages || 2;
  return (
    <motion.div initial={fadeInUp} animate={fadeInUpAnimate} className="dashboard-card p-6" data-testid="target-length-panel">
      <h3 className="font-satoshi text-lg font-bold text-[#0f1115] mb-2 flex items-center space-x-2">
        <FileText size={20} className="text-[#002fa7]" />
        <span>Resume Length</span>
      </h3>
      <p className="text-sm text-[#6b7280] mb-2">
        Changing this will re-optimize your resume. Our AI will tighten or expand bullets to fit the new page count.
      </p>
      <p className="text-xs text-[#9ca3af] italic mb-4">
        Note: Ascend does its best to match your target page length. If your original resume has a lot of content, the final
        download may spill slightly onto the next page — we prioritize keeping your experience intact over hard-truncating.
      </p>
      <div className="grid grid-cols-5 gap-1.5 mb-4">
        {[1, 2, 3, 4, 5].map(n => {
          const active = current === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              disabled={reoptimizing}
              className={`py-2 text-center border-2 transition-colors text-sm font-bold ${active ? 'border-[#002fa7] bg-[#002fa7]/5 text-[#002fa7]' : 'border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7]'} disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid={`change-target-pages-${n}`}
            >
              {n} pg
            </button>
          );
        })}
      </div>
      <div className="flex gap-2 pt-3 border-t border-[#e5e7eb]">
        <button onClick={onReoptimize} disabled={reoptimizing} className="btn-outline flex-1 flex items-center justify-center gap-2 disabled:opacity-50 text-sm py-2" data-testid="reoptimize-button">
          <RefreshCw size={16} className={reoptimizing ? 'animate-spin' : ''} />
          <span>{reoptimizing ? 'Analyzing…' : 'Re-optimize'}</span>
        </button>
        <button onClick={onToggleEdit} className="btn-outline flex-1 flex items-center justify-center gap-2 text-sm py-2" data-testid="edit-button">
          <Edit3 size={16} />
          <span>{editing ? 'Cancel' : 'Edit'}</span>
        </button>
      </div>
      {reoptimizing && (
        <div className="mt-3 text-xs text-[#002fa7] flex items-center gap-2" data-testid="target-length-loading">
          <LoaderIcon size={12} className="animate-spin" /> Re-optimizing for new length…
        </div>
      )}
    </motion.div>
  );
};

// Confirmation dialog for re-optimization after changing target pages
const ReoptimizeConfirmDialog = ({ open, currentPages, newPages, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onCancel}
      data-testid="reoptimize-dialog"
    >
      <div
        className="bg-white max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-satoshi text-xl font-bold text-[#0f1115] mb-3">Re-optimize resume for {newPages} page{newPages > 1 ? 's' : ''}?</h3>
        <p className="text-sm text-[#4b5563] mb-4">
          Your resume is currently targeting <strong>{currentPages} page{currentPages > 1 ? 's' : ''}</strong>.
          Changing to <strong>{newPages} page{newPages > 1 ? 's' : ''}</strong> will use AI to{' '}
          {newPages < currentPages ? 'tighten bullets and shorten your summary' : 'expand your experience with more detail'}.
        </p>
        <p className="text-xs text-[#6b7280] mb-5">This uses 1 AI optimization credit.</p>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 border border-[#e5e7eb] text-[#4b5563] text-sm font-semibold hover:bg-[#f4f5f7]" data-testid="reoptimize-cancel">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 bg-[#002fa7] text-white text-sm font-semibold hover:bg-[#001d6a]" data-testid="reoptimize-confirm">Re-optimize</button>
        </div>
      </div>
    </div>
  );
};

const ResumeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkGate, gateModal } = useFeatureGate();
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reoptimizing, setReoptimizing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [jobDescription, setJobDescription] = useState('');
  const [matchingJob, setMatchingJob] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [rewrites, setRewrites] = useState([]);
  const [rewriteSelections, setRewriteSelections] = useState([]);
  const [applyingRewrites, setApplyingRewrites] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [showRewrites, setShowRewrites] = useState(false);
  const [showJobMatcher, setShowJobMatcher] = useState(false);
  const [showReoptimizeDialog, setShowReoptimizeDialog] = useState(false);
  const [pendingTargetPages, setPendingTargetPages] = useState(null);
  const [showStudio, setShowStudio] = useState(false);
  const apiRef = useRef(API);

  const fetchResume = useCallback(async () => {
    try {
      const response = await axios.get(`${apiRef.current}/resumes/${id}`, { withCredentials: true });
      setResume(response.data);
      setEditData(response.data);
      setJobDescription(response.data.job_description || '');
      if (response.data.rewritten_bullets) {
        setRewrites(response.data.rewritten_bullets);
        setRewriteSelections(response.data.rewritten_bullets.map(() => true));
      }
    } catch (err) {
      logger.error('Fetch resume failed:', err.message);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchResume();
  }, [fetchResume]);

  const handleReoptimize = useCallback(async () => {
    setReoptimizing(true);
    try {
      const response = await axios.post(`${apiRef.current}/resumes/${id}/reoptimize`, {}, { withCredentials: true });
      setResume(response.data);
    } catch (err) {
      logger.error('Reoptimize failed:', err.message);
    } finally { setReoptimizing(false); }
  }, [id]);

  // Change target_pages on an existing resume → update + re-run AI rewrite to match new length
  const handleTargetPagesChange = useCallback(async (newPages) => {
    if (!resume || newPages === resume.target_pages) return;
    setPendingTargetPages(newPages);
    setShowReoptimizeDialog(true);
  }, [resume]);

  const confirmReoptimizeWithPages = useCallback(async () => {
    if (!pendingTargetPages) return;
    setShowReoptimizeDialog(false);
    setReoptimizing(true);
    try {
      // Save new target_pages, then call /resize which actually rewrites content via AI
      await axios.put(`${apiRef.current}/resumes/${id}`, { target_pages: pendingTargetPages }, { withCredentials: true });
      const fresh = await axios.post(`${apiRef.current}/resumes/${id}/resize`, {}, { withCredentials: true });
      setResume(fresh.data);
      setEditData(fresh.data);
      setRewrites(fresh.data.rewritten_bullets || []);
      setRewriteSelections((fresh.data.rewritten_bullets || []).map(() => true));
    } catch (err) {
      logger.error('Resize failed:', err.message);
    } finally {
      setReoptimizing(false);
      setPendingTargetPages(null);
    }
  }, [id, pendingTargetPages]);

  const cancelReoptimize = useCallback(() => {
    setShowReoptimizeDialog(false);
    setPendingTargetPages(null);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!window.confirm('Are you sure you want to delete this resume?')) return;
    try {
      await axios.delete(`${apiRef.current}/resumes/${id}`, { withCredentials: true });
      navigate('/dashboard');
    } catch (err) {
      logger.error('Delete failed:', err.message);
    }
  }, [id, navigate]);

  const handleSave = useCallback(async () => {
    try {
      const response = await axios.put(`${apiRef.current}/resumes/${id}`, editData, { withCredentials: true });
      setResume(response.data);
      setEditing(false);
    } catch (err) {
      logger.error('Save failed:', err.message);
    }
  }, [id, editData]);

  // NEW: Job Description Matcher
  const handleMatchJob = useCallback(async () => {
    if (!jobDescription.trim()) return;
    setMatchingJob(true);
    try {
      const response = await axios.post(
        `${apiRef.current}/resumes/${id}/match-job`,
        { job_description: jobDescription },
        { withCredentials: true }
      );
      setResume(response.data);
    } catch (err) {
      logger.error('Job match failed:', err.message);
    } finally { setMatchingJob(false); }
  }, [id, jobDescription]);

  // NEW: AI Rewriter
  const handleRewrite = useCallback(async () => {
    if (await checkGate('ai_rewrite')) return;
    setRewriting(true);
    try {
      const response = await axios.post(`${apiRef.current}/resumes/${id}/rewrite`, {}, { withCredentials: true });
      const rs = response.data.rewrites || [];
      setRewrites(rs);
      setRewriteSelections(rs.map(() => true));
      setApplyResult(null);
      setShowRewrites(true);
    } catch (err) {
      logger.error('Rewrite failed:', err.message);
    } finally { setRewriting(false); }
  }, [id, checkGate]);

  const toggleRewriteSelect = useCallback((index) => {
    setRewriteSelections(prev => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }, []);

  const toggleAllRewrites = useCallback((selectAll) => {
    setRewriteSelections(rewrites.map(() => !!selectAll));
  }, [rewrites]);

  const applySelectedRewrites = useCallback(async () => {
    const indices = rewriteSelections
      .map((sel, i) => (sel ? i : -1))
      .filter(i => i >= 0);
    if (indices.length === 0) return;
    setApplyingRewrites(true);
    setApplyResult(null);
    try {
      const res = await axios.post(
        `${apiRef.current}/resumes/${id}/apply-rewrites`,
        { indices },
        { withCredentials: true }
      );
      setApplyResult(res.data);
      // Refetch so content, rewrites, downloads all reflect the new state.
      const fresh = await axios.get(`${apiRef.current}/resumes/${id}`, { withCredentials: true });
      setResume(fresh.data);
      setEditData(fresh.data);
      setRewrites(fresh.data.rewritten_bullets || []);
      setRewriteSelections((fresh.data.rewritten_bullets || []).map(() => true));
      setShowApplyConfirm(false);
      if (window.gtag) window.gtag('event', 'apply_rewrites', { applied: res.data.applied, skipped: res.data.skipped });
    } catch (err) {
      logger.error('Apply rewrites failed:', err);
      window.alert(err.response?.data?.detail || 'Failed to apply rewrites. Please try again.');
    } finally {
      setApplyingRewrites(false);
    }
  }, [id, rewriteSelections]);

  // Copy full optimized resume to clipboard
  const [copied, setCopied] = useState(false);
  const [accentColor, setAccentColor] = useState('#002fa7');

  // Sections where every line should be treated as a bulleted skill/competency,
  // even if the AI didn't prefix each line with `-`
  const SKILL_LIST_SECTIONS = useMemo(() => new Set([
    'skills', 'technical skills', 'core competencies', 'areas of expertise',
    'key skills', 'competencies', 'specialties', 'expertise', 'tools', 'technologies'
  ]), []);

  // Pre-process raw text to avoid page bloat in Word:
  // 1) Collapse runs of 2+ blank lines to a single blank line
  // 2) Merge orphan bullet markers with the next content line
  // 3) Merge continuation lines (hard-wrapped sentences)
  const preprocessText = useCallback((text) => {
    // Step 0: collapse runs of 2+ blank lines to a single blank line
    const collapsed = text.replace(/(\r?\n\s*){2,}/g, '\n\n');
    const raw = collapsed.split('\n').map(l => l.trim());
    // Step 1: merge orphan bullet-only lines with the next content line
    const step1 = [];
    for (let i = 0; i < raw.length; i++) {
      const cur = raw[i];
      if (/^[-•*·▪►●◦]+$/.test(cur)) {
        let j = i + 1;
        while (j < raw.length && (!raw[j] || /^[-•*·▪►●◦]+$/.test(raw[j]))) j++;
        if (j < raw.length) {
          step1.push(`• ${raw[j]}`);
          i = j;
        }
        continue;
      }
      step1.push(cur);
    }

    // Step 2: merge continuation lines (hard-wrapped sentences)
    const step2 = [];
    const CONTINUATION_STARTERS = /^(and|but|or|nor|so|yet|to|with|for|in|at|on|by|via|of|across|through|while|including|such|a|an|the)\s/i;
    const isSectionHeaderLike = (s) => {
      if (!s) return false;
      const stripped = s.replace(/[:]+$/, '');
      return /^[A-Z][A-Z\s&/\-,]{2,38}$/.test(stripped) && stripped === stripped.toUpperCase();
    };
    for (let i = 0; i < step1.length; i++) {
      const cur = step1[i];
      if (!cur) { step2.push(cur); continue; }
      const prev = step2.length > 0 ? step2[step2.length - 1] : '';
      if (!prev) { step2.push(cur); continue; }

      const prevIsBullet = /^[-•*·▪►●◦]\s+/.test(prev);
      const prevIsHeader = isSectionHeaderLike(prev);
      const curIsBullet = /^[-•*·▪►●◦]\s+/.test(cur);
      const curIsHeader = isSectionHeaderLike(cur);
      // Previous ending in open paren, comma, slash, backslash, or unmatched bracket = continuation follows
      const prevEndsWithContinuator = /[(,/\\]$|^[^)]*\([^)]*$/.test(prev);
      const prevEndsWithTerminator = /[.!?:]$/.test(prev);
      const prevEndsWithDash = /[-–—]$/.test(prev);
      const firstChar = cur.charAt(0);
      const startsLowercase = !!(firstChar && firstChar === firstChar.toLowerCase() && firstChar.match(/[a-z]/));
      const startsWithContinuation = CONTINUATION_STARTERS.test(cur);
      // Cur starts with closing paren / bracket = continuation
      const startsWithCloser = /^[)\]}]/.test(cur);

      // Skip merging if either side is a header, or cur is a new bullet / subheading
      if (prevIsHeader || curIsHeader || curIsBullet) {
        step2.push(cur);
        continue;
      }

      // A line containing a "label | value" pattern is its own subheading — don't merge
      const curHasPipeOrDash = cur.includes('|') || cur.includes(' — ') || cur.includes(' – ');

      // Lines that look like structured reference items (certs, skills, education lines)
      // should NOT merge even without terminal punctuation. Pattern: has " | " separator.
      const prevIsStructuredItem = prev.includes(' | ') || prev.includes(' — ') || prev.includes(' – ');

      const shouldMerge =
        // Previous line leaves an unclosed clause open
        prevEndsWithContinuator ||
        // Previous ends with hyphen (e.g., "state-of-the-art")
        prevEndsWithDash ||
        // Current starts with "(" — a new parenthetical that's always a continuation
        /^[(]/.test(cur) ||
        // Current is an obvious continuation (starts lowercase, with conjunction, or closing bracket)
        ((!prevEndsWithTerminator) && (startsLowercase || startsWithContinuation || startsWithCloser)) ||
        // LIBERAL FALLBACK: prev doesn't end with terminal punctuation AND cur is not a new structural
        // element (header, subheading, bullet) — treat as continuation. Protects against AI hard-wraps
        // that start with uppercase (e.g., "Proven success implementing\nSingle Sign-On (SSO)").
        (!prevEndsWithTerminator && !curHasPipeOrDash && !prevIsBullet && !prevIsStructuredItem);

      if (shouldMerge && !curHasPipeOrDash) {
        step2[step2.length - 1] = prev.replace(/[-–—]$/, '').trimEnd() + ' ' + cur;
      } else if (shouldMerge && curHasPipeOrDash && prevEndsWithContinuator) {
        // Edge case: previous is open parenthetical and cur has "|" — still merge
        step2[step2.length - 1] = prev.replace(/[-–—]$/, '').trimEnd() + ' ' + cur;
      } else {
        step2.push(cur);
      }
    }

    return step2.join('\n');
  }, []);

  // Parse resume content into structured sections for formatting
  const parseResumeContent = useCallback((text) => {
    const lines = text.split('\n');
    const sections = [];
    let currentSection = null;

    // Known section titles (case-insensitive match)
    const KNOWN_SECTIONS = [
      'professional summary', 'summary', 'objective', 'profile',
      'experience', 'work experience', 'professional experience', 'employment history',
      'education', 'academic background',
      'skills', 'technical skills', 'core competencies', 'areas of expertise', 'key skills',
      'competencies', 'specialties', 'expertise', 'tools', 'technologies',
      'certifications', 'licenses & certifications', 'licenses and certifications',
      'projects', 'key projects', 'notable projects',
      'awards', 'honors', 'achievements', 'honors & awards',
      'volunteer', 'volunteer experience', 'community involvement',
      'languages', 'interests', 'hobbies', 'publications', 'references'
    ];

    const isKnownSection = (s) => {
      const lower = s.toLowerCase().replace(/[:]+$/, '').trim();
      return KNOWN_SECTIONS.includes(lower);
    };

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      // Section header = known title OR short ALL-CAPS line
      const isAllCaps = /^[A-Z][A-Z\s&/\-,]{2,38}$/.test(trimmed) && trimmed === trimmed.toUpperCase();
      const isHeader = isKnownSection(trimmed) || isAllCaps;

      if (isHeader) {
        const cleanTitle = trimmed.replace(/[:]+$/, '').trim();
        currentSection = { title: cleanTitle, items: [] };
        sections.push(currentSection);
      } else if (currentSection) {
        currentSection.items.push(trimmed);
      } else {
        if (!sections.length || sections[0].title !== '__header__') {
          sections.unshift({ title: '__header__', items: [] });
        }
        sections[0].items.push(trimmed);
      }
    }
    return sections;
  }, []);

  const getOptimizedContent = useCallback(() => {
    let content = resume?.content || '';
    for (const r of rewrites) {
      if (r.original && r.improved) {
        content = content.replace(r.original, r.improved);
      }
    }
    // NEVER truncate the clipboard output — always copy the FULL resume.
    // Max Pages is a guidance for AI re-optimization, NOT a hard text cap.
    // Truncating here cuts off sections like Education, which is unacceptable.
    return content;
  }, [resume?.content, rewrites]);

  // Escape HTML special characters
  const escapeHtml = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const buildFormattedHTML = useCallback(() => {
    const rawText = getOptimizedContent();
    const text = preprocessText(rawText);
    const sections = parseResumeContent(text);

    // Word-safe font stack. Word honors font-family, color, font-size, font-weight
    // on <p>/<li>/<h1>/<h2>/<strong>. Word STRIPS text-transform, so uppercase must
    // be applied to the literal text.
    const baseFont = "'Calibri', 'Segoe UI', Arial, sans-serif";

    // Compact inline styles with MSO hints to suppress Word's default large margins
    const msoCompact = 'mso-margin-top-alt:auto; mso-margin-bottom-alt:auto;';

    let bodyInner = '';
    const headerSection = sections.find(s => s.title === '__header__');
    const bodySections = sections.filter(s => s.title !== '__header__');

    // Detect whether a pre-section line is contact info vs prose
    // Contact info contains: email, phone, URL, or is short & comma-separated (city/state)
    const isContactLike = (line) => {
      if (!line) return false;
      const hasEmail = /\S+@\S+\.\S+/.test(line);
      const hasPhone = /\(?\d{3}\)?[\s\-.]?\d{3}[\s\-.]?\d{4}/.test(line);
      const hasURL = /(https?:\/\/|www\.|linkedin\.com|github\.com|\.com\/|\.io\/|\.dev)/i.test(line);
      // Short line with location pattern "City, ST" or a job title
      const isShortLocation = line.length < 60 && /[A-Z][a-z]+,\s*[A-Z]{2}/.test(line);
      return hasEmail || hasPhone || hasURL || isShortLocation;
    };

    // Helper: wrap text with <font color> + inline style for Word dark-mode resilience
    const coloredText = (text, color, extraStyle = '') => {
      const safe = escapeHtml(text);
      return `<font color="${color}"><span style="color:${color}; ${extraStyle}">${safe}</span></font>`;
    };

    // ===== Header (Name + Contact + implicit Summary) =====
    if (headerSection && headerSection.items.length > 0) {
      const items = headerSection.items;
      const name = items[0];

      // Classify remaining items: contact-like lines go into contact block,
      // everything else is treated as an implicit Professional Summary
      const contactLines = [];
      const summaryLines = [];
      for (let k = 1; k < items.length; k++) {
        const ln = items[k];
        if (isContactLike(ln) && summaryLines.length === 0) {
          contactLines.push(ln);
        } else {
          summaryLines.push(ln);
        }
      }

      // Name
      bodyInner += `<p style="font-family:${baseFont}; font-size:22pt; font-weight:700; margin:0 0 2pt 0; padding:0; line-height:1.1; ${msoCompact}">${coloredText(name, accentColor, 'font-weight:700;')}</p>`;

      // Contact line (joined with pipes)
      if (contactLines.length > 0) {
        bodyInner += `<p style="font-family:${baseFont}; font-size:10pt; color:#555555; margin:0 0 6pt 0; padding:0; line-height:1.2; ${msoCompact}"><font color="#555555">${escapeHtml(contactLines.join(' | '))}</font></p>`;
      }

      // Accent divider
      bodyInner += `<p style="font-family:${baseFont}; border-bottom:1.5pt solid ${accentColor}; margin:0 0 8pt 0; padding:0; font-size:1pt; line-height:1pt; ${msoCompact}">&nbsp;</p>`;

      // Implicit Summary — render as its own section
      if (summaryLines.length > 0) {
        bodyInner += `<p class="asc-section-break" style="font-family:${baseFont}; font-size:11pt; font-weight:700; letter-spacing:1pt; padding:0 0 1pt 0; border-bottom:1pt solid ${accentColor}; line-height:1.2;">${coloredText('PROFESSIONAL SUMMARY', accentColor, 'font-weight:700; letter-spacing:1pt;')}</p>`;
        for (const line of summaryLines) {
          bodyInner += `<p style="font-family:${baseFont}; font-size:11pt; color:#222222; line-height:1.3;"><font color="#222222">${escapeHtml(line)}</font></p>`;
        }
      }
    }

    // ===== Body Sections =====
    for (const section of bodySections) {
      // Uppercase the title literally so Word renders it in caps (Word strips text-transform)
      const uppercaseTitle = section.title.toUpperCase();
      bodyInner += `<p class="asc-section-break" style="font-family:${baseFont}; font-size:11pt; font-weight:700; letter-spacing:1pt; padding:0 0 1pt 0; border-bottom:1pt solid ${accentColor}; line-height:1.2;">${coloredText(uppercaseTitle, accentColor, 'font-weight:700; letter-spacing:1pt;')}</p>`;

      const sectionKey = section.title.toLowerCase();
      const isSkillSection = SKILL_LIST_SECTIONS.has(sectionKey);

      // Skill-list sections: render EVERY non-empty item as a bullet, even without "-" prefix
      if (isSkillSection && section.items.length > 0) {
        bodyInner += `<ul style="font-family:${baseFont}; font-size:11pt; color:#222222; margin:3pt 0 6pt 0; padding-left:20pt; list-style-type:disc; ${msoCompact}">`;
        for (const raw of section.items) {
          // Strip leading bullet chars if present so we don't double-bullet
          const clean = raw.replace(/^[-•*·▪►●◦]\s*/, '').trim();
          if (!clean) continue;
          bodyInner += `<li style="font-family:${baseFont}; font-size:11pt; color:#222222; margin:0 0 1pt 0; padding:0; line-height:1.25; ${msoCompact}"><font color="#222222">${escapeHtml(clean)}</font></li>`;
        }
        bodyInner += `</ul>`;
        continue;
      }

      // Experience/Projects/etc: detect bullets vs subheadings vs paragraphs
      let i = 0;
      while (i < section.items.length) {
        const item = section.items[i];
        const isBullet = /^[-•*·▪►●◦]\s+/.test(item);

        if (isBullet) {
          // Collect consecutive bullets into a single <ul>
          const bulletGroup = [];
          while (i < section.items.length && /^[-•*·▪►●◦]\s+/.test(section.items[i])) {
            const b = section.items[i].replace(/^[-•*·▪►●◦]\s*/, '').trim();
            if (b) bulletGroup.push(b);
            i++;
          }
          if (bulletGroup.length > 0) {
            bodyInner += `<ul style="font-family:${baseFont}; font-size:11pt; color:#222222; margin:3pt 0 6pt 0; padding-left:20pt; list-style-type:disc; ${msoCompact}">`;
            for (const b of bulletGroup) {
              bodyInner += `<li style="font-family:${baseFont}; font-size:11pt; color:#222222; margin:0 0 2pt 0; padding:0; line-height:1.3; ${msoCompact}"><font color="#222222">${escapeHtml(b)}</font></li>`;
            }
            bodyInner += `</ul>`;
          }
          continue;
        }

        // Subheading: pipe-separated or em-dash-separated, short line
        const hasPipe = item.includes('|');
        const hasEmDash = item.includes(' — ') || item.includes(' – ');
        const isSubheading = (hasPipe || hasEmDash) && item.length < 160;

        if (isSubheading) {
          const sep = hasPipe ? '|' : (item.includes(' — ') ? ' — ' : ' – ');
          const parts = item.split(sep).map(p => p.trim()).filter(Boolean);
          const first = parts[0];
          const rest = parts.slice(1).join(' | ');
          bodyInner += `<p style="font-family:${baseFont}; font-size:11pt; color:#0f1115; margin:6pt 0 1pt 0; padding:0; line-height:1.25; ${msoCompact}"><font color="#0f1115"><strong style="font-weight:700;">${escapeHtml(first)}</strong></font>`;
          if (rest) {
            bodyInner += `<font color="#555555"><span style="color:#555555; font-weight:400;"> &nbsp;|&nbsp; ${escapeHtml(rest)}</span></font>`;
          }
          bodyInner += `</p>`;
        } else {
          // Regular paragraph (summary text)
          bodyInner += `<p style="font-family:${baseFont}; font-size:11pt; color:#222222; margin:0 0 4pt 0; padding:0; line-height:1.35; ${msoCompact}"><font color="#222222">${escapeHtml(item)}</font></p>`;
        }
        i++;
      }
    }

    // CRITICAL: Word ignores inline `margin` on paragraphs and applies its own
    // default "Space After: 8pt" style — which is what creates the double-spacing
    // users see when pasting. The ONLY reliable way to kill that is a <style>
    // block with `mso-para-margin-*` properties scoped to our wrapper class.
    // This is what Microsoft-generated HTML uses internally and it's the single
    // biggest win against Word page-bloat.
    const wordKillStyles = `
      <style>
        .asc-rx, .asc-rx p, .asc-rx li, .asc-rx div, .asc-rx span, .asc-rx table, .asc-rx h1, .asc-rx h2, .asc-rx h3 {
          mso-pagination: widow-orphan;
          mso-style-unhide: no;
        }
        .asc-rx p, .asc-rx div.asc-line {
          margin: 0 0 2pt 0;
          padding: 0;
          mso-margin-top-alt: auto;
          mso-margin-bottom-alt: auto;
          mso-para-margin-top: 0in;
          mso-para-margin-right: 0in;
          mso-para-margin-bottom: 0in;
          mso-para-margin-left: 0in;
          mso-line-height-rule: exactly;
          line-height: 1.25;
        }
        .asc-rx ul {
          margin: 2pt 0 4pt 0;
          padding-left: 20pt;
          mso-margin-top-alt: auto;
          mso-margin-bottom-alt: auto;
          mso-para-margin-top: 0in;
          mso-para-margin-bottom: 0in;
          list-style-type: disc;
        }
        .asc-rx li {
          margin: 0 0 1pt 0;
          padding: 0;
          mso-margin-top-alt: auto;
          mso-margin-bottom-alt: auto;
          mso-para-margin-top: 0in;
          mso-para-margin-bottom: 0in;
          mso-line-height-rule: exactly;
          line-height: 1.25;
        }
        .asc-rx .asc-section-break {
          margin: 8pt 0 2pt 0;
          mso-para-margin-top: 0.08in;
          mso-para-margin-bottom: 0.02in;
        }
      </style>
    `;

    // Outer wrapper — sets default font for pasted block
    return `${wordKillStyles}<div class="asc-rx" style="font-family:${baseFont}; color:#1a1a1a; font-size:11pt; line-height:1.25;">${bodyInner}</div>`;
  }, [getOptimizedContent, preprocessText, parseResumeContent, accentColor, SKILL_LIST_SECTIONS]);

  const handleCopy = useCallback(async () => {
    try {
      const html = buildFormattedHTML();
      const plainText = getOptimizedContent();

      // Use Clipboard API for rich HTML copy
      const htmlBlob = new Blob([html], { type: 'text/html' });
      const textBlob = new Blob([plainText], { type: 'text/plain' });
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob,
        }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      // Fallback for browsers that don't support ClipboardItem
      try {
        await navigator.clipboard.writeText(getOptimizedContent());
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      } catch (fallbackErr) {
        logger.error('Copy failed:', fallbackErr);
      }
    }
  }, [buildFormattedHTML, getOptimizedContent]);

  const handlePreview = useCallback(() => {
    const html = buildFormattedHTML();
    const previewWindow = window.open('', '_blank');
    if (!previewWindow) return;
    const doc = previewWindow.document;
    doc.open();
    const root = doc.createElement('html');
    const head = doc.createElement('head');
    const title = doc.createElement('title');
    title.textContent = `${resume?.title || 'Resume'} — Preview`;
    head.appendChild(title);
    const style = doc.createElement('style');
    style.textContent = "body { display: flex; justify-content: center; padding: 40px 20px; background: #f0f0f0; } @media print { body { background: white; padding: 0; } }";
    head.appendChild(style);
    root.appendChild(head);
    const body = doc.createElement('body');
    const wrapper = doc.createElement('div');
    wrapper.style.cssText = 'background: white; padding: 48px 56px; max-width: 800px; width: 100%; box-shadow: 0 2px 20px rgba(0,0,0,0.1);';
    wrapper.innerHTML = html;
    body.appendChild(wrapper);
    root.appendChild(body);
    doc.appendChild(root);
    doc.close();
  }, [buildFormattedHTML, resume?.title]);

  const handleDownload = useCallback(async (fmt) => {
    if (!resume?.resume_id) return;
    try {
      const html = buildFormattedHTML();
      const res = await axios.post(
        `${API}/resumes/${resume.resume_id}/export`,
        { html, fmt },
        { withCredentials: true, responseType: 'blob' }
      );
      // Derive filename from server's Content-Disposition when possible
      let filename = `${(resume.title || 'Resume').replace(/[^\w\-\s]/g, '').trim().replace(/\s+/g, '_')}.${fmt}`;
      const disp = res.headers?.['content-disposition'];
      if (disp) {
        const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp);
        if (m && m[1]) filename = decodeURIComponent(m[1]);
      }
      const blob = new Blob([res.data], {
        type: fmt === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      if (window.gtag) window.gtag('event', 'download_resume', { format: fmt });
    } catch (err) {
      logger.error('download failed', err);
      window.alert('Download failed. Please try again in a moment.');
    }
  }, [buildFormattedHTML, resume?.resume_id, resume?.title]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] pt-20 flex items-center justify-center">
        <Loader2 size={48} className="animate-spin text-[#002fa7]" />
      </div>
    );
  }

  if (!resume) return null;

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20" data-testid="resume-detail-page">
      <ReoptimizeConfirmDialog
        open={showReoptimizeDialog}
        currentPages={resume.target_pages || 2}
        newPages={pendingTargetPages || 2}
        onConfirm={confirmReoptimizeWithPages}
        onCancel={cancelReoptimize}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <button onClick={() => navigate('/dashboard')} className="inline-flex items-center space-x-2 text-[#4b5563] hover:text-[#002fa7] mb-4" data-testid="back-button">
              <ArrowLeft size={20} /><span>Back to Dashboard</span>
            </button>
            <h1 className="font-satoshi text-3xl font-black tracking-tight text-[#0f1115]">{resume.title}</h1>
            <div className="flex items-center space-x-4 mt-2 text-[#6b7280]">
              <span className="flex items-center space-x-1"><Target size={16} /><span>{resume.target_job}</span></span>
              {resume.target_company && <span className="flex items-center space-x-1"><Building size={16} /><span>{resume.target_company}</span></span>}
              <span className="flex items-center space-x-1"><Calendar size={16} /><span>{new Date(resume.updated_at).toLocaleDateString()}</span></span>
            </div>
          </div>
          <div className="flex items-center space-x-3 mt-4 md:mt-0">
            <button onClick={handleDelete} className="p-3 border border-[#e11d48] text-[#e11d48] hover:bg-[#e11d48] hover:text-white transition-colors" data-testid="delete-button">
              <Trash2 size={18} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-1 space-y-6">
            {/* Score Cards */}
            <motion.div initial={fadeInUp} animate={fadeInUpAnimate} className="dashboard-card p-6">
              <div className="grid grid-cols-2 gap-4">
                <ScoreGauge score={resume.ats_score} label="ATS Score" />
                {resume.job_match_score && (
                  <ScoreGauge score={resume.job_match_score} label="Job Match" />
                )}
              </div>
              <ShareScoreButton resume={resume} />
            </motion.div>

            {/* Target Length Panel */}
            <TargetLengthPanel
              resume={resume}
              reoptimizing={reoptimizing}
              onChange={handleTargetPagesChange}
              onReoptimize={handleReoptimize}
              onToggleEdit={() => setEditing(!editing)}
              editing={editing}
            />

            <JobMatcherPanel
              showJobMatcher={showJobMatcher} setShowJobMatcher={setShowJobMatcher}
              jobDescription={jobDescription} setJobDescription={setJobDescription}
              matchingJob={matchingJob} handleMatchJob={handleMatchJob}
            />

            <AIRewriterPanel
              rewriting={rewriting} handleRewrite={handleRewrite}
              rewrites={rewrites} showRewrites={showRewrites} setShowRewrites={setShowRewrites}
            />

            <CopyResumePanel
              copied={copied} handleCopy={handleCopy} handlePreview={handlePreview}
              hasRewrites={rewrites.length > 0}
              accentColor={accentColor} setAccentColor={setAccentColor}
              handleDownload={handleDownload}
              onOpenStudio={() => setShowStudio(true)}
            />

            <KeywordsPanel
              title="Keywords Matched" keywords={resume.keywords_matched}
              icon={CheckCircle} iconColor="text-[#10b981]"
              tagBg="bg-[#d1fae5]" tagText="text-[#065f46]"
              emptyMessage="No keywords matched yet"
            />

            <KeywordsPanel
              title="Keywords Missing" keywords={resume.keywords_missing}
              icon={XCircle} iconColor="text-[#e11d48]"
              tagBg="bg-[#fee2e2]" tagText="text-[#991b1b]"
              emptyMessage="All important keywords present!"
            />

            <SuggestionsPanel suggestions={resume.suggestions} />
          </div>

          {/* Right Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* AI Rewrites Panel */}
            {showRewrites && rewrites.length > 0 && (
              <motion.div initial={fadeInUp} animate={fadeInUpAnimate} className="dashboard-card p-6" data-testid="rewrites-panel">
                <h3 className="font-satoshi text-lg font-bold text-[#0f1115] mb-2 flex items-center space-x-2">
                  <Wand2 size={20} className="text-[#10b981]" />
                  <span>AI-Rewritten Bullet Points</span>
                </h3>
                <p className="text-sm text-[#6b7280] mb-4">
                  Review each suggestion. Check the ones you want to keep, then click
                  <strong className="text-[#0f1115]"> Apply Selected Rewrites</strong> to
                  bake them into your resume automatically — no copy-paste needed.
                </p>

                {/* Pre-apply warning banner */}
                <div className="mb-4 p-3 bg-[#fef3c7] border border-[#fcd34d] text-[#78350f] text-sm flex items-start gap-2" data-testid="apply-warning">
                  <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Want to keep the current optimized resume too?</strong>{' '}
                    Download or copy it first — applying rewrites updates your resume in place.
                  </div>
                </div>

                {/* Bulk select controls */}
                <div className="flex items-center justify-between mb-3 text-sm">
                  <span className="text-[#4b5563] font-semibold" data-testid="rewrite-select-count">
                    {rewriteSelections.filter(Boolean).length} of {rewrites.length} selected
                  </span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => toggleAllRewrites(true)} className="text-[#002fa7] hover:underline" data-testid="select-all-rewrites">Select all</button>
                    <span className="text-[#d1d5db]">|</span>
                    <button onClick={() => toggleAllRewrites(false)} className="text-[#002fa7] hover:underline" data-testid="select-none-rewrites">Select none</button>
                  </div>
                </div>

                {rewrites.map((rewrite, index) => (
                  <RewriteCard
                    key={`rewrite-${index}`}
                    rewrite={rewrite}
                    index={index}
                    selected={!!rewriteSelections[index]}
                    onToggleSelect={toggleRewriteSelect}
                  />
                ))}

                {/* Apply button */}
                <div className="mt-4 pt-4 border-t border-[#e5e7eb]">
                  <button
                    onClick={() => setShowApplyConfirm(true)}
                    disabled={rewriteSelections.filter(Boolean).length === 0 || applyingRewrites}
                    className="w-full py-3 bg-[#10b981] text-white font-bold hover:bg-[#059669] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    data-testid="apply-rewrites-btn"
                  >
                    {applyingRewrites ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                    <span>
                      {applyingRewrites
                        ? 'Regenerating resume...'
                        : `Apply ${rewriteSelections.filter(Boolean).length} selected & regenerate resume`}
                    </span>
                  </button>
                </div>

                {/* Post-apply result */}
                {applyResult && (
                  <div className="mt-3 p-3 bg-[#d1fae5] border border-[#10b981] text-[#065f46] text-sm" data-testid="apply-result">
                    <strong>Done.</strong> Applied {applyResult.applied} of {applyResult.total} rewrites.
                    {applyResult.skipped > 0 && (
                      <span> {applyResult.skipped} couldn't be matched to the original text and were skipped.</span>
                    )}{' '}
                    Your updated resume is ready to download or copy above.
                  </div>
                )}
              </motion.div>
            )}

            {/* Confirm Apply Rewrites Dialog */}
            {showApplyConfirm && (
              <div
                className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
                onClick={() => setShowApplyConfirm(false)}
                data-testid="apply-rewrites-dialog"
              >
                <div className="bg-white max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-start gap-3 mb-4">
                    <AlertCircle size={24} className="text-[#f59e0b] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-satoshi text-lg font-bold text-[#0f1115]">Apply {rewriteSelections.filter(Boolean).length} rewrites?</h3>
                      <p className="text-sm text-[#4b5563] mt-1">
                        This will update your resume content with the selected rewrites and make them the new baseline.
                      </p>
                      <p className="text-sm text-[#78350f] mt-3 p-2 bg-[#fef3c7] border border-[#fcd34d]">
                        <strong>Tip:</strong> If you want to keep a copy of the current version,
                        download or copy it before confirming — this action replaces it in place.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2">
                    <button
                      onClick={() => setShowApplyConfirm(false)}
                      className="px-4 py-2 border border-[#e5e7eb] text-[#4b5563] hover:bg-[#f4f5f7] transition-colors"
                      data-testid="apply-cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={applySelectedRewrites}
                      disabled={applyingRewrites}
                      className="px-4 py-2 bg-[#10b981] text-white font-semibold hover:bg-[#059669] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                      data-testid="apply-confirm-btn"
                    >
                      {applyingRewrites ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                      <span>Yes, apply rewrites</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Resume Content */}
            <motion.div initial={fadeInUp} animate={fadeInUpAnimate} transition={delayedTransition} className="dashboard-card p-6">
              <h3 className="font-satoshi text-lg font-bold text-[#0f1115] mb-4">Resume Content</h3>
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0f1115] mb-2">Title</label>
                    <input type="text" value={editData.title} onChange={(e) => setEditData({ ...editData, title: e.target.value })} className="w-full px-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7]" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0f1115] mb-2">Target Job</label>
                      <input type="text" value={editData.target_job} onChange={(e) => setEditData({ ...editData, target_job: e.target.value })} className="w-full px-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7]" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0f1115] mb-2">Target Company</label>
                      <input type="text" value={editData.target_company || ''} onChange={(e) => setEditData({ ...editData, target_company: e.target.value })} className="w-full px-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0f1115] mb-2">Content</label>
                    <textarea value={editData.content} onChange={(e) => setEditData({ ...editData, content: e.target.value })} rows={20} className="w-full px-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] font-mono text-sm" />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button onClick={() => { setEditing(false); setEditData(resume); }} className="btn-outline">Cancel</button>
                    <button onClick={handleSave} className="btn-primary" data-testid="save-button">Save Changes</button>
                  </div>
                </div>
              ) : (
                <pre className="whitespace-pre-wrap font-mono text-sm text-[#4b5563] bg-[#f4f5f7] p-6 max-h-[600px] overflow-y-auto" data-testid="resume-content">
                  {resume.content}
                </pre>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {showStudio && (
        <ResumeStudio
          resumeId={resume.resume_id}
          onClose={() => {
            setShowStudio(false);
            // Refetch to pick up any inline-edit saves from Studio
            fetchResume();
          }}
        />
      )}
      {gateModal}
    </div>
  );
};

export default ResumeDetail;
